import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { AddRoleMap } from '../../../../models/shared/add-role-map';
import { AddRemoveRoleFormViewModel } from './add-remove-role-form-view-model';
import { BaseUser } from '../../../../models/base/base-user';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { Subscribable } from '../../../../models/base/subscribable';

@Component({
  selector: 'app-add-remove-role-form',
  templateUrl: './add-remove-role-form.component.html',
  styleUrls: ['./add-remove-role-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddRemoveRoleFormComponent),
      multi: true
    },
    AddRemoveRoleFormViewModel
  ]
})
export class AddRemoveRoleFormComponent extends Subscribable implements OnChanges, AfterViewInit, ControlValueAccessor {
  @Input() controlId: string = '';
  @Input() addRoleMap: AddRoleMap = new AddRoleMap();
  @Input() atLeastNItemsFilledIn: number = 1;
  // Ensure your user object has a roles property, and implements UniquelyIdentifiable
  @Input() user: BaseUser = {} as BaseUser;
  @Input() submitAttempted: boolean = false;
  @Input() gridLayout: boolean = false;

  private viewModel = inject(AddRemoveRoleFormViewModel);
  private fb = inject(FormBuilder);

  public canEditUserDetails$ = this.viewModel.canEditUserDetails$;
  public roleDropdownOptions$ = this.viewModel.roleDropdownOptions$;
  public hideAddRoleButton$ = this.viewModel.hideAddRoleButton$;

  public formGroup = this.fb.group({
    roles: this.fb.array<number>([], Validators.required)
  });

  get rolesFormArray(): FormArray {
    return this.formGroup.get('roles') as FormArray;
  }

  public value: number[] = [];

  writeValue(obj: any): void {
    if (obj !== undefined) {
      this.value = obj;
      this.onChange(this.value);
    }
  }

  public onChange = (value: any) => {};
  public onTouched = () => {};

  public addAdditionalRole(index: number): void {
    this.rolesFormArray.push(
      new FormControl<number | null>(null, {
        nonNullable: true,
        validators: [Validators.required]
      })
    );
    this.viewModel.addAdditionalRole(index);
  }

  public removeRole(index: number): void {
    if (this.rolesFormArray.length > this.atLeastNItemsFilledIn) {
      this.rolesFormArray.removeAt(index);
      this.viewModel.removeRole(index);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.addRoleMap) this.viewModel.setRoleMap(this.addRoleMap);
    if (changes.user) {
      this.viewModel.setUser(this.user);
      this.initRoles();
    }
  }

  public ngAfterViewInit(): void {
    this.formGroup.valueChanges.subscribeWhileAlive({
      owner: this,
      next: value => {
        value.roles?.forEach((roleId, index) => {
          if (roleId) this.viewModel.roleSelected(roleId, index);
        });
        this.writeValue(value.roles);
      }
    });
  }

  private initRoles(): void {
    this.rolesFormArray.clear();
    const userRoles = this.user?.roles;

    userRoles.forEach(role => {
      this.rolesFormArray.push(
        new FormControl<number | null>(role.roleId, {
          nonNullable: true,
          validators: [Validators.required]
        })
      );
    });

    if (this.rolesFormArray.length === 0) {
      this.rolesFormArray.push(
        new FormControl<number | null>(null, {
          nonNullable: true,
          validators: [Validators.required]
        })
      );
    }
    this.formGroup.updateValueAndValidity();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
